import { useCallback, useState } from 'react';

import { useAuthInfo } from '@/allauth_auth/hooks';
import { useOrg } from '@/contexts/OrgContext';
import type { DashboardSectionPrefs } from '@/lib/localStorage/dashboardSectionPrefs';
import {
  getDashboardSectionPrefs,
  updateDashboardSectionPref,
} from '@/lib/localStorage/dashboardSectionPrefs';

export function useDashboardSectionPref<K extends keyof DashboardSectionPrefs>(
  key: K,
  defaultValue: DashboardSectionPrefs[K]
): [DashboardSectionPrefs[K], (next: DashboardSectionPrefs[K]) => void] {
  const { user } = useAuthInfo();
  const { currentOrg } = useOrg();
  const userId = user?.id;
  const orgId = currentOrg?.id;

  const [value, setValue] = useState<DashboardSectionPrefs[K]>(() => {
    const prefs = getDashboardSectionPrefs(userId, orgId);
    return prefs[key] ?? defaultValue;
  });

  // The preference is scoped to (user, org). Both can resolve after the first
  // render (a cold boot, an org switch), so re-read it when the scope changes —
  // during render, not in an effect: an effect would paint the default variant
  // for one frame and then jump to the stored one (REACT_19_PATTERNS § 4.2).
  const [scope, setScope] = useState({ userId, orgId });
  if (scope.userId !== userId || scope.orgId !== orgId) {
    setScope({ userId, orgId });
    const prefs = getDashboardSectionPrefs(userId, orgId);
    setValue(prefs[key] ?? defaultValue);
  }

  const updateValue = useCallback(
    (next: DashboardSectionPrefs[K]) => {
      if (userId != null && orgId != null) {
        updateDashboardSectionPref(userId, orgId, key, next);
      }
      setValue(next);
    },
    [userId, orgId, key]
  );

  return [value, updateValue] as const;
}
